import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {
  AnalyticsDashboardSpecResponse,
  getRestTypeForGraphQLParameterConfig,
} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import {
  convertGQLToRESTAnalyticsQuery,
  convertRESTToGQLAnalyticsQuery,
  GraphQLAnalyticsQuerySpecFragment,
} from 'insights/analytics-queries/data/AnalyticsQueryGQLRESTConverters';
import {
  convertGQLToRESTLayoutRow,
  convertRESTToGQLLayoutRow,
  GraphQLLayoutRowSpecFragment,
} from 'insights/layouts/LayoutRowGQLRESTConverters';
import {
  convertGQLToRESTParameterValue,
  convertRESTToGQLParameterValue,
} from 'insights/parameters/data/ParameterGQLRESTConverters';
import {getConfigForRESTValue} from 'insights/parameters/data/ValueGetters';
import {AnalyticsParameterValueFragment} from 'insights/parameters/ParameterConstants';
import omitKey from 'util/omitKey';
import pickDefined from 'util/pickDefined';

import {GraphQLAnalyticsDashboardSpec} from './__generated/GraphQLAnalyticsDashboardSpec';

const GRAPHQL_ANALYTICS_DASHBOARD_SPEC_FRAGMENT: DocumentNode = gql`
  fragment GraphQLAnalyticsDashboardSpec on AnalyticsDashboardNode {
    id
    analyticsQueries {
      ...GraphQLAnalyticsQuerySpec
    }
    description
    name
    modifiedAt
    layoutRows {
      ...GraphQLLayoutRowSpec
    }
    parameterConfigs {
      ...AnalyticsParameterConfigGraphQL
    }
    parameterValues {
      ...AnalyticsParameterValue
    }
    permissions {
      admin
      appendable
      deletable
      movable
      readable
      readable
      writable
    }
    project {
      id
    }
    url
  }

  ${getFragmentWrapper('AnalyticsParameterConfig').fragment}
  ${AnalyticsParameterValueFragment.fragment}
  ${GraphQLAnalyticsQuerySpecFragment.fragment}
  ${GraphQLLayoutRowSpecFragment.fragment}
`;

export const GraphQLAnalyticsDashboardSpecFragment: FragmentWrapper<GraphQLAnalyticsDashboardSpec> = {
  fragment: GRAPHQL_ANALYTICS_DASHBOARD_SPEC_FRAGMENT,
  fragmentName: 'GraphQLAnalyticsDashboardSpec',
};

export function convertGQLToRESTAnalyticsDashboard(
  gqlAnalyticsDashboard: GraphQLAnalyticsDashboardSpec
): AnalyticsDashboardSpecResponse {
  const {analyticsQueries, layoutRows, modifiedAt, parameterConfigs, parameterValues, project} =
    gqlAnalyticsDashboard;
  return {
    ...pickDefined(gqlAnalyticsDashboard, 'id', 'description', 'name', 'url'),
    analyticsQueries: analyticsQueries.map((analyticsQuery) =>
      convertGQLToRESTAnalyticsQuery(analyticsQuery)
    ),
    modifiedAt,
    layoutRows: layoutRows.map((row) => convertGQLToRESTLayoutRow(row)),
    parameterConfigsGQL: parameterConfigs,
    parameterValues: parameterValues.map((value) => convertGQLToRESTParameterValue(value)),
    projectId: project.id,
    permissions: {
      ...omitKey(gqlAnalyticsDashboard.permissions, '__typename'),
      movable: Boolean(gqlAnalyticsDashboard.permissions.movable),
      deletable: Boolean(gqlAnalyticsDashboard.permissions.deletable),
    },
  };
}

export function convertRESTToGQLAnalyticsDashboard(
  restAnalyticsDashboard: AnalyticsDashboardSpecResponse
): GraphQLAnalyticsDashboardSpec {
  const {
    analyticsQueries,
    modifiedAt,
    layoutRows,
    parameterConfigsGQL,
    parameterValues,
    projectId,
    permissions,
  } = restAnalyticsDashboard;
  return {
    __typename: 'AnalyticsDashboardNode',
    ...pickDefined(restAnalyticsDashboard, 'id', 'description', 'name', 'url'),
    analyticsQueries: analyticsQueries.map((analyticsQuery) =>
      convertRESTToGQLAnalyticsQuery(analyticsQuery)
    ),
    layoutRows: layoutRows.map((row) => convertRESTToGQLLayoutRow(row)),
    modifiedAt,
    parameterConfigs: parameterConfigsGQL,
    parameterValues: parameterValues.map((value) =>
      convertRESTToGQLParameterValue(
        value,
        getRestTypeForGraphQLParameterConfig(getConfigForRESTValue(parameterConfigsGQL, value))
      )
    ),
    project: {__typename: 'ProjectNode', id: projectId},
    permissions: {
      __typename: 'PermissionsNode',
      ...permissions,
      deletable: Boolean(permissions.deletable),
      movable: Boolean(permissions.movable),
    },
  };
}
